import { Plus, Trash2 } from "lucide-react";
import type { CalendarException } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { minutesToTime, timeToMinutes } from "./time-helpers";

interface Props {
  value: CalendarException[];
  onChange: (exceptions: CalendarException[]) => void;
  disabled?: boolean;
}

// Data de hoje em AAAA-MM-DD (hora local) p/ default de nova exceção — sem drift de fuso.
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Editor das exceções datadas (CLOSED derruba o dia; EXTRA_WINDOW adiciona janela).
// Janela extra é mesmo-dia na UI (fim > início). Reusado no cenário.
export function ExceptionsList({ value, onChange, disabled }: Props) {
  const patch = (i: number, next: CalendarException) =>
    onChange(value.map((e, idx) => (idx === i ? next : e)));
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));

  const changeKind = (i: number, kind: CalendarException["kind"]) => {
    const e = value[i]!;
    if (kind === "CLOSED") patch(i, { kind: "CLOSED", date: e.date, reason: e.reason });
    else
      patch(i, {
        kind: "EXTRA_WINDOW",
        date: e.date,
        startMinute: 480,
        endMinute: 720,
        reason: e.reason,
      });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[13px] font-medium">Exceções datadas</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...value, { kind: "CLOSED", date: todayLocal() }])}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Exceção
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-subtle px-3 py-3 text-center text-[12px] text-text-tertiary">
          Nenhum feriado ou hora extra cadastrado.
        </p>
      ) : (
        <ul className="space-y-2">
          {value.map((exc, i) => {
            const isExtra = exc.kind === "EXTRA_WINDOW";
            const invalid = isExtra && exc.endMinute <= exc.startMinute;
            return (
              <li
                key={i}
                className={cn(
                  "flex flex-wrap items-center gap-2 rounded-md border px-2 py-2",
                  invalid ? "border-rd-danger-500" : "border-border-subtle"
                )}
              >
                <Input
                  aria-label="Data"
                  type="date"
                  className="h-9 w-[150px]"
                  value={exc.date}
                  disabled={disabled}
                  onChange={(e) => patch(i, { ...exc, date: e.target.value })}
                />
                <Select
                  aria-label="Tipo de exceção"
                  className="h-9 w-[128px]"
                  value={exc.kind}
                  disabled={disabled}
                  onChange={(e) => changeKind(i, e.target.value as CalendarException["kind"])}
                >
                  <option value="CLOSED">Fechado</option>
                  <option value="EXTRA_WINDOW">Hora extra</option>
                </Select>
                {isExtra ? (
                  <>
                    <Input
                      aria-label="Início da janela extra"
                      type="time"
                      className="h-9 w-[104px]"
                      value={minutesToTime(exc.startMinute)}
                      disabled={disabled}
                      onChange={(e) =>
                        patch(i, { ...exc, startMinute: timeToMinutes(e.target.value) })
                      }
                    />
                    <span className="text-text-tertiary">–</span>
                    <Input
                      aria-label="Fim da janela extra"
                      type="time"
                      className="h-9 w-[104px]"
                      value={minutesToTime(exc.endMinute)}
                      disabled={disabled}
                      onChange={(e) =>
                        patch(i, { ...exc, endMinute: timeToMinutes(e.target.value) })
                      }
                    />
                  </>
                ) : null}
                <Input
                  aria-label="Motivo"
                  placeholder="Motivo (opcional)"
                  className="h-9 min-w-[120px] flex-1"
                  value={exc.reason ?? ""}
                  disabled={disabled}
                  onChange={(e) => patch(i, { ...exc, reason: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remover exceção"
                  disabled={disabled}
                  onClick={() => remove(i)}
                >
                  <Trash2 className="h-4 w-4 text-rd-danger-500" />
                </Button>
                {invalid ? (
                  <p className="w-full text-[11px] text-rd-danger-500" role="alert">
                    O fim da janela deve ser depois do início.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
